import React, { useEffect, useState } from 'react'
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  OutlinedInput,
} from '@mui/material'
import '../../styles/inputBox.css'
import WorkIcon from '../../assets/images/JobIcon/work-icon.svg'
import OverviewIcon from '../../assets/images/JobIcon/Overview.svg'
import GoalIcon from '../../assets/images/JobIcon/goal.svg'
import CheckIcon from '../../assets/images/JobIcon/check-bold.svg'
import SymbolsIcon from '../../assets/images/JobIcon/symbols_step.svg'
import TeamsIcon from '../../assets/images/JobIcon/team-people.svg'
import { useNavigate, useParams } from 'react-router-dom'
import { getRequest, postRequest, putRequest } from '../../api/apiServices'
import {
  createJobSummary,
  getJobSummaryByJobId,
  updateJobSummaryByJobId,
} from '../../api/apiList'
import { generateTexts } from '../../utils/AIGenerateText'
import Loader from '../../utils/Loading'
import Toast from '../../utils/Toast'
import { roleBasedRedirection } from '../../utils/helper'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/store'

interface JobProfileSixthProps {
  onBack: () => void
  onNext: () => void
  initialData: any // Function to navigate to the next component
  mode: any
}

const JobProfileSixth: React.FC<JobProfileSixthProps> = ({
  initialData,
  mode,
}: JobProfileSixthProps) => {
  //params
  const params = useParams()
  const [hiringManagerId, setHiringManagerId] = useState('')
  const [overview, setOverview] = useState('')
  const [responsibilities, setResponsibilities] = useState('')
  const [challenges, setChallenges] = useState('')
  const [team, setTeam] = useState('')
  const [interview, setInterview] = useState('')
  const [loading, setLoading] = useState(false)
  const isMobile = useMediaQuery('(max-width:578px)')
  const isTabs = useMediaQuery('(max-width:1200px)')
  const navigate = useNavigate()
  console.log('initialData: ', initialData)

  const handleSubmit = async (event: any) => {
    event.preventDefault()
    const data: any = {
      jobId: initialData.id,
      hiringManagerId: initialData.hiringManagerId,
      overview,
      responsibilities,
      challenges,
      team,
      interview,
      companyId: initialData.companyId,
    }

    if (mode) {
      setLoading(true)
      if (Number(hiringManagerId) !== Number(initialData.hiringManagerId)) {
        data.isAccepted = false
      }

      const response = await putRequest(
        updateJobSummaryByJobId(params?.id),
        data
      )
      setLoading(false)
      if (response) {
        Toast(
          response?.data?.statusMessage || 'Job Summary Updated Successfully'
        )
        // navigate("/jobs");
        navigate(roleBasedRedirection('/jobs'))
      }
    } else {
      setLoading(true)
      const response = await postRequest(createJobSummary, data)
      setLoading(false)
      if (response) {
        Toast(
          response?.data?.statusMessage || 'Job Summary Create Successfully'
        )
        // navigate("/jobs");
        navigate(roleBasedRedirection('/jobs'))
      }
    }
  }

  const handleOverviewResponse = async () => {
    const dataString = `
You are an expert career assistant AI.

Job Title: ${initialData?.jobTitle}

TASK:
Create a clear, concise job overview that reflects the actual nature of the job title. 
Describe the purpose, responsibilities, and expectations based ONLY on the job title. 
Do not add unrelated duties or generic information.

OUTPUT RULES:
- 5 to 8 bullet points only
- Each bullet starts with "- "
- No headings, numbers, emojis, or markdown
- Do not repeat the job title inside bullets
- No filler text, no repetition
- Keep the response professional and complete
- Ensure no sentence is cut off
- Stay within 300 tokens
`
    const reponse = await generateTexts(dataString)
    return reponse
  }

  const handleReponsibilityResponse = async () => {
    const dataString = `
You are an expert career assistant AI.

Job Title: ${initialData?.jobTitle}

SOURCE (use ONLY this):
Responsibilities: ${initialData?.taskOfThisPosition}
Technical Skills: ${initialData?.technicalSkillsRequired}

TASK:
Create up to 6 bullet points that summarize the main responsibilities of this role. 
Use the SOURCE only. 
Do not copy sentences directly; rewrite them clearly and professionally.
Do not invent new responsibilities.

OUTPUT RULES:
- Bullets only, each starting with "- "
- No headings, numbers, emojis, or markdown
- Each bullet must be unique and non-repetitive
- Keep the response complete and within 300 tokens
`
    const reponse = await generateTexts(dataString)
    return reponse
  }

  const handleChalengesResponse = async () => {
    const dataString = `
You are an expert career assistant AI.

Job Title: ${initialData?.jobTitle}

SOURCE (use ONLY this):
Role Challenges: ${initialData?.roleChallenges}

TASK:
Create up to 6 bullet points describing real, relevant challenges for this role. 
Use ONLY the SOURCE. 
Do not add new challenges that are not implied by the SOURCE.
Rewrite in clear, professional language.

OUTPUT RULES:
- Bullets only, each starting with "- "
- No headings, numbers, emojis, or markdown
- No repeated points
- Keep the response complete and under 300 tokens
`
    const reponse = await generateTexts(dataString)
    return reponse
  }

  const handleInterviewResponse = async () => {
    const interviewSource = Array.isArray(initialData?.interviewProcess)
      ? initialData.interviewProcess
          .map((item: any) =>
            `${item.stepNumber ? item.stepNumber + '. ' : ''}${
              item.content || ''
            } ${item.customContent || ''}`.trim()
          )
          .join(' | ')
      : initialData?.interviewProcess || ''

    const dataString = `
You are an expert career assistant AI.

Job Title: ${initialData?.jobTitle}

SOURCE (use ONLY this):
Interview Process: ${interviewSource}

TASK:
Create up to 6 bullet points describing the interview steps, based entirely on the SOURCE. 
Rewrite the content for clarity and structure. 
Do NOT add steps or guess missing stages.

OUTPUT RULES:
- Bullets only, each starting with "- "
- No headings, numbers, emojis, or markdown
- No repetition; each bullet must express a distinct step
- Keep the response complete and under 300 tokens
`
    const reponse = await generateTexts(dataString)
    return reponse
  }

  const handleTeamResponse = async () => {
    const dataString = `
You are an expert career assistant AI.

Job Title: ${initialData?.jobTitle}

SOURCE (use ONLY this):
Team Structure: ${initialData?.teamInteraction}
Culture: ${initialData?.cultureFit}

TASK:
First, produce up to 4 bullet points describing how the team is structured for this role.
Then produce up to 4 bullet points describing culture expectations and attributes needed.
Use ONLY the SOURCE fields. Do not add or assume anything not included.

OUTPUT RULES:
- Bullets only, each starting with "- "
- Team-interaction bullets appear first
- Culture bullets appear after, with no section titles
- No headings, numbers, emojis, or markdown
- No repetition; every bullet must be unique
- Keep the response complete and under 300 tokens
`
    const reponse = await generateTexts(dataString)
    return reponse
  }

  //OnLoad Set the Data of Job Summery
  useEffect(() => {
    if (!params.id) {
      fetchData()
    }
    if (params.id) {
      fetchJobSummary()
    }
  }, [params.id])

  const fetchData = async () => {
    try {
      setLoading(true)

      const [
        overviewData,
        responsibilityResponse,
        challengesResponse,
        teamResponse,
        interviewResponse,
      ] = await Promise.all([
        handleOverviewResponse(),
        handleReponsibilityResponse(),
        handleChalengesResponse(),
        handleTeamResponse(),
        handleInterviewResponse(),
      ])

      setOverview(overviewData)
      setResponsibilities(responsibilityResponse)
      setChallenges(challengesResponse)
      setTeam(teamResponse)
      setInterview(interviewResponse)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.error('Error fetching data:', error)
    }
  }

  const fetchJobSummary = async () => {
    setLoading(true)
    const response = await getRequest(getJobSummaryByJobId(params?.id))
    setLoading(false)
    if (response) {
      const responses = response?.data?.data
      setHiringManagerId(responses.hiringManagerId)
      setOverview(responses.overview)
      setResponsibilities(responses.responsibilities)
      setChallenges(responses.challenges)
      setTeam(responses.team)
      setInterview(responses.interview)
    }
  }

  return loading ? (
    <Loader />
  ) : (
    <div>
      <Container
        style={{
          display: 'flex',
          padding: '25px',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Grid
          spacing={2}
          bgcolor="#FFFFFF"
          width="884px"
          borderRadius="22px"
          border="1px solid #D6D6D7"
          margin={0}
        >
          <Grid
            item
            height={isMobile ? 'auto' : 'auto'}
            style={{ padding: '23px 16px' }}
          >
            <Grid
              container
              display="flex"
              bgcolor="#84E1CC"
              borderRadius="17px"
              padding="18px"
            >
              <Grid item display="flex" gap="20px">
                <img src={WorkIcon} alt="team-icon" />
                <Grid display="flex" alignItems="center">
                  <Typography
                    color="#FFFFFF"
                    fontWeight={700}
                    fontSize="20.94px"
                  >
                    {initialData?.jobTitle} - Summary
                  </Typography>
                  <Grid mb="1px"></Grid>
                  <Typography
                    color="#FFFFFF"
                    fontWeight={500}
                    fontSize="12.94px"
                  ></Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid mt="35px"></Grid>
            <Grid
              display="flex"
              direction={isMobile ? 'column' : 'row'}
              alignItems="center"
              gap="10px"
            >
              <Grid>
                <Box
                  padding="17.50px"
                  borderRadius="7px"
                  width="fit-content"
                  bgcolor="#84E1CC"
                >
                  <img src={OverviewIcon} width="32px" height="32px" />
                </Box>
              </Grid>
              {/* {overview ? ( */}
              <OutlinedInput
                placeholder="Type here..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={overview}
                onChange={(e) => {
                  const value = e.target.value
                  setOverview(value)
                }}
              />
              {/* // ) : (
              //   <Loader />
              // )} */}
            </Grid>
            <Grid mt="61px"></Grid>
            <Grid
              display="flex"
              alignItems="center"
              direction={isMobile ? 'column' : 'row'}
              gap="10px"
            >
              <Grid>
                <Box
                  padding="17.50px"
                  borderRadius="7px"
                  width="fit-content"
                  bgcolor="#84E1CC"
                >
                  <img src={CheckIcon} width="32px" height="32px" />
                </Box>
              </Grid>
              {/* {responsibilities ? ( */}
              <OutlinedInput
                placeholder="Type here..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={responsibilities}
                onChange={(e) => {
                  const value = e.target.value
                  setResponsibilities(value)
                }}
              />
              {/* // ) : (
              //   <Loader />
              // )} */}
            </Grid>
            <Grid mt="61px"></Grid>
            <Grid
              display="flex"
              alignItems="center"
              direction={isMobile ? 'column' : 'row'}
              gap="10px"
            >
              <Grid>
                <Box
                  padding="17.50px"
                  borderRadius="7px"
                  width="fit-content"
                  bgcolor="#84E1CC"
                >
                  <img src={GoalIcon} width="32px" height="32px" />
                </Box>
              </Grid>

              {/* {challenges ? ( */}
              <OutlinedInput
                placeholder="Type here..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={challenges}
                onChange={(e) => {
                  const value = e.target.value
                  setChallenges(value)
                }}
              />
              {/* // ) : (
              //   <Loader />
              // )} */}
            </Grid>
            <Grid mt="61px"></Grid>
            <Grid
              display="flex"
              alignItems="center"
              direction={isMobile ? 'column' : 'row'}
              gap="10px"
            >
              <Grid>
                <Box
                  padding="17.50px"
                  borderRadius="7px"
                  width="fit-content"
                  bgcolor="#84E1CC"
                >
                  <img src={TeamsIcon} width="32px" height="32px" />
                </Box>
              </Grid>
              {/* {team ? ( */}
              <OutlinedInput
                placeholder="Type here..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={team}
                onChange={(e) => {
                  const value = e.target.value
                  setTeam(value)
                }}
              />
              {/* // ) : (
              //   <Loader />
              // )} */}
            </Grid>
            <Grid mt="61px"></Grid>
            <Grid
              display="flex"
              alignItems="center"
              direction={isMobile ? 'column' : 'row'}
              gap="10px"
            >
              <Grid>
                <Box
                  padding="17.50px"
                  borderRadius="7px"
                  width="fit-content"
                  bgcolor="#84E1CC"
                >
                  <img src={SymbolsIcon} width="32px" height="32px" />
                </Box>
              </Grid>
              {/* {interview ? ( */}
              <OutlinedInput
                placeholder="Type here..."
                multiline
                minRows={4}
                maxRows={10}
                className="text-area-size"
                value={interview}
                onChange={(e) => {
                  const value = e.target.value
                  setInterview(value)
                }}
              />
              {/* // ) : (
              //   <Loader />
              // )} */}
            </Grid>
            <Grid mt="43px"></Grid>
            <Grid
              container
              direction={isMobile ? 'column' : 'row'}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              padding={isMobile ? '' : '0px 34px 0px 34px'}
              gap={isMobile ? '10px' : ''}
            >
              {/* <Grid> */}
              {/* <button
                  type="submit"
                  className="user-scale"
                  // variant="contained"
                  // fullWidth
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #000000",
                    color: "#273848",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    letterSpacing: "0px",
                    textTransform: "none",
                    width: "100%",
                    // textTransform:'lowercase',
                  }}
                  onClick={(event) => handleBack(event)}
                >
                  Add Informations
                </button> */}
              {/* </Grid> */}
              <Grid>
                <button
                  type="submit"
                  className="user-scale"
                  // variant="contained"
                  // fullWidth
                  style={{
                    backgroundColor: '#84E1CC',
                    boxShadow: 'none',
                    borderRadius: '5px',
                    padding: isTabs ? '19px 55px' : '17px 17px',
                    lineHeight: '0px',
                    letterSpacing: '0px',
                    textTransform: 'none',
                    border: 'none',
                    color: 'white',
                    width: ' 100%',
                  }}
                  onClick={(event) => handleSubmit(event)}
                >
                  Sent to Hiring Manager
                </button>
              </Grid>
            </Grid>
            <Grid mb="10px"></Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default JobProfileSixth
